import { Helmet } from 'react-helmet-async';
import { getCanonicalPath } from '@/lib/canonical';
import { generateBreadcrumbSchema } from '@/lib/schemaGenerator';

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string; // e.g. "/airport-transportation"
  ogImage?: string;
  schema?: any;
  noindex?: boolean; // For filter/search pages that shouldn't be indexed
  includeBreadcrumbs?: boolean; // Automatically generate breadcrumb schema from path
  keywords?: string; // Comma-separated keywords for meta keywords tag
}

// Generate breadcrumb items from path
const generateBreadcrumbItems = (path: string) => {
  const items = [{ name: 'Home', url: '/' }];
  
  if (path === '/' || path === '') return items;
  
  const segments = path.split('/').filter(Boolean);
  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Create human-readable names from URL segments
    const name = segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    items.push({ name, url: currentPath });
  });
  
  return items;
};

function SEO({ 
  title, 
  description, 
  canonicalPath = "/",
  ogImage = "https://ricksblackcarservicesantonio.com/og-image.jpg",
  schema,
  noindex = false,
  includeBreadcrumbs = true,
  keywords
}: SEOProps) {
  const siteUrl = "https://ricksblackcarservicesantonio.com";
  
  // Normalize canonical path (lowercase, no trailing slash except root, no params)
  const normalizedPath = getCanonicalPath(canonicalPath);
  const canonicalUrl = `${siteUrl}${normalizedPath}`;
  
  // Generate breadcrumb schema if requested
  const breadcrumbSchema = includeBreadcrumbs 
    ? generateBreadcrumbSchema(generateBreadcrumbItems(normalizedPath))
    : null;
  
  // Combine breadcrumb schema with provided schema
  const allSchemas = breadcrumbSchema
    ? (schema ? (Array.isArray(schema) ? [breadcrumbSchema, ...schema] : [breadcrumbSchema, schema]) : breadcrumbSchema)
    : schema;

  return (
    <Helmet>
      {/* Robots meta - noindex for filter/search pages, index for content */}
      <meta name="robots" content={noindex ? "noindex,follow" : "index,follow"} />
      <meta name="googlebot" content={noindex ? "noindex,follow" : "index,follow"} />
      
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Rick's Black Car Service" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {allSchemas && (
        Array.isArray(allSchemas) ? (
          allSchemas.map((schemaItem, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schemaItem)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(allSchemas)}
          </script>
        )
      )}
    </Helmet>
  );
}

export default SEO;
